import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';

import { experiences } from '../../data/index.js';
import { SectionWrapper } from '../../hoc/index.js';
import { styles } from '../../styles.js';
import { fadeIn, staggerContainer, textVariant } from '../../utils/motion.js';
import SelectedExperienceDetails from './SelectedExperienceDetails.jsx';
import Modal from './Modal.jsx';
import TimelineItem from './TimelineItem.jsx';

const Experience = () => {
  const { t } = useTranslation();
  const controls = useAnimation();
  const initialExperience = experiences.length > 0 ? experiences[0] : null;
  const [selectedJob, setSelectedJob] = useState(initialExperience);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
  const [direction, setDirection] = useState(1);
  const manualInteraction = useRef(false);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let intervalId;
    if (inView && isDesktop) {
      const intervalId = setInterval(() => {
        if (manualInteraction.current) {
          return;
        }

        setDirection(1);
        setSelectedJob((currentJob) => {
          if (!currentJob) return experiences[0];
          const currentIndex = experiences.findIndex(
            (exp) => exp.company_name === currentJob.company_name
          );
          const nextIndex = (currentIndex + 1) % experiences.length;
          return experiences[nextIndex];
        });
      }, 7000);

      return () => clearInterval(intervalId);
    }
  }, [inView, isDesktop, selectedJob]);

  const handleTimelineItemClick = (experience) => {
    if (selectedJob?.company_name === experience.company_name) return;

    manualInteraction.current = true;

    const currentIndex = experiences.findIndex(
      (exp) => exp.company_name === selectedJob?.company_name
    );
    const newIndex = experiences.findIndex(
      (exp) => exp.company_name === experience.company_name
    );
    setDirection(newIndex > currentIndex ? 1 : -1);
    setSelectedJob(experience);

    setTimeout(() => {
      manualInteraction.current = false;
    }, 100);

    if (window.innerWidth < 768) {
      setIsModalOpen(true);
    }
  };

  const detailUpdateVariants = {
    hidden: (direction) => ({ opacity: 0, x: direction > 0 ? 50 : -50 }),
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 20,
        duration: 0.5,
      },
    },
    exit: (direction) => ({
      opacity: 0,
      x: direction > 0 ? -50 : 50,
      transition: { duration: 0.3 },
    }),
  };
  const detailInitialVariant = fadeIn('left', 'spring', 0.3, 0.8);

  useEffect(() => {
    if (inView) {
      controls.start('show');
    }
  }, [controls, inView]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div ref={ref} className="px-4 sm:px-6 pb-10 md:pb-16">
      <motion.div variants={textVariant()} initial="hidden" animate={controls}>
        <h2 className={`${styles.sectionText} text-center`}>
          {t('nav.experience')}
        </h2>
      </motion.div>
      <div className="mt-8 md:mt-20 lg:mt-28 flex flex-col md:flex-row gap-8 md:gap-12 lg:gap-16">
        <div className="w-full my-8 md:my-0 md:w-[40%] lg:w-[35%] xl:w-[30%] relative">
          <div
            className="absolute left-8 md:left-6 top-0 bottom-0 w-0.5 md:w-1 bg-gradient-to-b from-transparent
                        via-slate-600 to-transparent transform -translate-x-1/2"
            aria-hidden="true"
          ></div>
          <motion.div
            variants={staggerContainer(0.1, 0.2)}
            initial="hidden"
            animate={controls}
            className="relative z-10 flex flex-col px-4 md:px-0"
          >
            {experiences.map((experience, index) => (
              <TimelineItem
                key={`experience-${index}-${experience.company_name}`}
                experience={experience}
                onClick={() => handleTimelineItemClick(experience)}
                isActive={selectedJob?.company_name === experience.company_name}
              />
            ))}
          </motion.div>
        </div>
        <motion.div
          variants={detailInitialVariant}
          initial="hidden"
          animate={controls}
          className="w-full md:w-[60%] lg:w-[65%] xl:w-[70%] hidden md:block md:self-start relative"
        >
          <AnimatePresence mode="wait" custom={direction}>
            {selectedJob && (
              <motion.div
                key={selectedJob.company_name}
                custom={direction}
                variants={detailUpdateVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="w-full"
              >
                <SelectedExperienceDetails experience={selectedJob} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
      {typeof document !== 'undefined' &&
        ReactDOM.createPortal(
          <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
            {selectedJob && (
              <SelectedExperienceDetails experience={selectedJob} />
            )}
          </Modal>,
          document.body
        )}
    </div>
  );
};

export default SectionWrapper(Experience, 'experience');
